import React, { useState, useEffect, useCallback } from "react";
import {
  Calendar, Users, Clock, Zap, AlertTriangle, Loader2, ChevronLeft,
  ChevronRight, Brain, MapPin, BarChart3, UserCheck
} from "lucide-react";
import VendorSchedulerPanel from "./VendorSchedulerPanel";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SmartSchedulerTab = ({ authAxios }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(0);
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [assigning, setAssigning] = useState("");
  const [manualSel, setManualSel] = useState({});
  const [reassignOpen, setReassignOpen] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/scheduler/capacity`);
      setData(r.data);
    } catch { /* silent */ }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAutoAssign = async (bookingId) => {
    setAssigning(bookingId);
    try {
      const r = await authAxios.post(`${API_URL}/api/scheduler/auto-assign`, { booking_id: bookingId });
      alert(`Assigned to ${r.data.assigned} — ${r.data.reason}`);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Assignment failed");
    }
    setAssigning("");
  };

  const handleManualAssign = async (bookingId) => {
    const opsName = manualSel[bookingId];
    if (!opsName) { alert("Please select a decorator first"); return; }
    setAssigning(bookingId);
    try {
      const r = await authAxios.post(`${API_URL}/api/scheduler/manual-assign`, { booking_id: bookingId, ops_name: opsName });
      alert(`Assigned to ${r.data.assigned}${r.data.over_capacity ? " — note: over daily capacity" : ""}`);
      setReassignOpen(s => ({ ...s, [bookingId]: false }));
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Assignment failed");
    }
    setAssigning("");
  };

  const handleAiOptimize = async () => {
    if (!data?.days?.[selectedDay]) return;
    setAiLoading(true);
    try {
      const r = await authAxios.post(`${API_URL}/api/scheduler/ai-optimize`, {
        date: data.days[selectedDay].date,
      });
      setAiSuggestion(r.data.suggestions || "No suggestions");
    } catch { setAiSuggestion("AI optimization failed"); }
    setAiLoading(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#DC283C]" size={28} /></div>;
  if (!data) return <p className="text-sm text-[#94a3b8] text-center py-20">Failed to load scheduler</p>;

  const day = data.days?.[selectedDay];

  return (
    <div className="space-y-5" data-testid="smart-scheduler">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#1E1B4B]">Smart Scheduler</h2>
          <p className="text-xs text-[#94a3b8]">{data.total_team} decorators, max {data.max_per_decorator}/day each</p>
        </div>
        <button onClick={handleAiOptimize} disabled={aiLoading}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-[#7C3AED] to-[#E83E8C] text-white text-xs font-bold hover:opacity-90 disabled:opacity-50"
          data-testid="ai-optimize-btn">
          {aiLoading ? <Loader2 size={13} className="animate-spin" /> : <Brain size={13} />}
          AI Optimize Day
        </button>
      </div>

      {/* Vendor Assignment (mandatory same-pincode + optional) — separate from OPS-member assignment */}
      <VendorSchedulerPanel authAxios={authAxios} />

      {/* 14-day capacity overview */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => setSelectedDay(d => Math.max(0, d - 1))} disabled={selectedDay <= 0}
            className="p-1 rounded-lg border border-gray-200 disabled:opacity-30"><ChevronLeft size={14} /></button>
          <p className="text-xs font-bold text-[#1E1B4B] flex-1 text-center">{day?.label}</p>
          <button onClick={() => setSelectedDay(d => Math.min(13, d + 1))} disabled={selectedDay >= 13}
            className="p-1 rounded-lg border border-gray-200 disabled:opacity-30"><ChevronRight size={14} /></button>
        </div>

        {/* Mini calendar strip */}
        <div className="flex gap-1 overflow-x-auto pb-2">
          {(data.days || []).map((d, i) => {
            const util = d.utilization_pct;
            const bg = d.overbooked ? "bg-red-500" : util > 75 ? "bg-amber-400" : util > 40 ? "bg-emerald-400" : "bg-gray-200";
            return (
              <button key={d.date} onClick={() => setSelectedDay(i)}
                className={`flex-shrink-0 w-12 py-2 rounded-lg text-center transition-all ${i === selectedDay ? "ring-2 ring-[#DC283C] ring-offset-1" : ""}`}
                style={{ background: i === selectedDay ? "#1E1B4B" : "#F9FAFB" }}
                data-testid={`day-${d.date}`}>
                <p className={`text-[8px] font-bold ${i === selectedDay ? "text-white" : "text-[#94a3b8]"}`}>{d.label.split(",")[0]}</p>
                <p className={`text-[10px] font-bold ${i === selectedDay ? "text-white" : "text-[#1E1B4B]"}`}>{d.label.split(" ")[1]}</p>
                <div className={`w-6 h-1.5 mx-auto mt-1 rounded-full ${bg}`} />
              </button>
            );
          })}
        </div>
      </div>

      {day && (
        <>
          {/* Day Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl border border-gray-100 p-3.5">
              <Calendar size={14} className="text-[#DC283C] mb-1" />
              <p className="text-lg font-bold text-[#1E1B4B]">{day.total_bookings}</p>
              <p className="text-[9px] text-[#94a3b8]">Bookings</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-3.5">
              <Users size={14} className="text-[#3B82F6] mb-1" />
              <p className="text-lg font-bold text-[#1E1B4B]">{day.available}</p>
              <p className="text-[9px] text-[#94a3b8]">Slots Available</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-3.5">
              <BarChart3 size={14} className="text-[#F97316] mb-1" />
              <p className={`text-lg font-bold ${day.utilization_pct > 80 ? "text-red-500" : "text-[#1E1B4B]"}`}>{day.utilization_pct}%</p>
              <p className="text-[9px] text-[#94a3b8]">Utilization</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-3.5">
              {day.overbooked ? <AlertTriangle size={14} className="text-red-500 mb-1" /> : <Zap size={14} className="text-emerald-500 mb-1" />}
              <p className={`text-lg font-bold ${day.overbooked ? "text-red-500" : "text-emerald-500"}`}>{day.overbooked ? "OVER" : "OK"}</p>
              <p className="text-[9px] text-[#94a3b8]">{day.overbooked ? "Overbooking!" : "Capacity"}</p>
            </div>
          </div>

          {/* Time Slot Heatmap */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Clock size={14} className="text-[#7C3AED]" /> Time Slots</h3>
            <div className="grid grid-cols-6 sm:grid-cols-11 gap-1.5">
              {(data.time_slots || []).map(slot => {
                const count = day.slot_usage?.[slot] || 0;
                const isHot = day.hot_slots?.includes(slot);
                const intensity = count / Math.max(data.total_team, 1);
                return (
                  <div key={slot} className={`text-center p-2 rounded-lg border ${isHot ? "border-red-300 bg-red-50" : "border-gray-100"}`}>
                    <p className="text-[8px] text-[#94a3b8] font-medium">{slot.replace(":00 ", "")}</p>
                    <div className="w-full h-5 mt-1 rounded flex items-center justify-center"
                      style={{ background: count > 0 ? `rgba(220,40,60,${0.15 + intensity * 0.6})` : "#F9FAFB" }}>
                      <span className={`text-[10px] font-bold ${intensity > 0.5 ? "text-white" : count > 0 ? "text-[#DC283C]" : "text-gray-300"}`}>{count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Team Load */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><Users size={14} className="text-[#3B82F6]" /> Team Load</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {Object.entries(day.team_load || {}).map(([name, load]) => (
                <div key={name} className={`p-3 rounded-xl border ${load.available <= 0 ? "border-red-200 bg-red-50" : "border-gray-100"}`}>
                  <p className="text-xs font-bold text-[#1E1B4B]">{name}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(load.bookings / load.max) * 100}%`, background: load.available <= 0 ? "#EF4444" : "#3B82F6" }} />
                    </div>
                    <span className="text-[10px] font-bold text-[#1E1B4B]">{load.bookings}/{load.max}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Day Bookings with Manual + Auto-Assign */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="text-sm font-bold text-[#1E1B4B] mb-3 flex items-center gap-2"><MapPin size={14} className="text-[#10B981]" /> Bookings ({day.total_bookings})</h3>
            <div className="space-y-2">
              {(day.bookings || []).map(b => {
                const teamList = Object.entries(day.team_load || {});
                const showPicker = !b.assigned_ops_name || reassignOpen[b.id];
                return (
                  <div key={b.id} className="flex flex-col gap-2 p-3 rounded-xl border border-gray-100 hover:bg-gray-50/50">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-[#1E1B4B]">{b.customer_name} — {b.package}</p>
                        <p className="text-[10px] text-[#94a3b8]">{b.setup_time} | {b.area || "—"} | {b.occasion}</p>
                      </div>
                      {b.assigned_ops_name && (
                        <div className="flex items-center gap-1.5">
                          <span className="flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-bold">
                            <UserCheck size={10} /> {b.assigned_ops_name}
                          </span>
                          <button onClick={() => setReassignOpen(s => ({ ...s, [b.id]: !s[b.id] }))}
                            className="px-2.5 py-1 rounded-lg border border-gray-200 text-[10px] font-bold text-[#475569] hover:bg-gray-50"
                            data-testid={`reassign-toggle-${b.id}`}>
                            {reassignOpen[b.id] ? "Cancel" : "Change"}
                          </button>
                        </div>
                      )}
                    </div>

                    {showPicker && (
                      <div className="flex flex-wrap items-center gap-2">
                        <select value={manualSel[b.id] || ""}
                          onChange={e => setManualSel(s => ({ ...s, [b.id]: e.target.value }))}
                          className="flex-1 min-w-[160px] px-2.5 py-1.5 rounded-lg border border-gray-200 text-[11px] font-medium text-[#1E1B4B] bg-white focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
                          data-testid={`manual-assign-select-${b.id}`}>
                          <option value="">Select decorator to assign…</option>
                          {teamList.map(([name, load]) => (
                            <option key={name} value={name}>
                              {name} ({load.bookings}/{load.max}){load.available <= 0 ? " — full" : ""}
                            </option>
                          ))}
                        </select>
                        <button onClick={() => handleManualAssign(b.id)}
                          disabled={assigning === b.id || !manualSel[b.id]}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[#10B981] text-white rounded-lg text-[10px] font-bold hover:bg-[#059669] disabled:opacity-40"
                          data-testid={`manual-assign-btn-${b.id}`}>
                          {assigning === b.id ? <Loader2 size={10} className="animate-spin" /> : <UserCheck size={10} />}
                          Assign
                        </button>
                        {!b.assigned_ops_name && (
                          <button onClick={() => handleAutoAssign(b.id)} disabled={assigning === b.id}
                            className="flex items-center gap-1 px-3 py-1.5 bg-[#7C3AED] text-white rounded-lg text-[10px] font-bold hover:bg-[#6D28D9] disabled:opacity-50"
                            data-testid={`auto-assign-${b.id}`}>
                            {assigning === b.id ? <Loader2 size={10} className="animate-spin" /> : <Zap size={10} />}
                            Auto-Assign
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {(day.bookings || []).length === 0 && (
                <p className="text-xs text-[#94a3b8] text-center py-4">No bookings on this date</p>
              )}
            </div>
          </div>

          {/* AI Optimization Results */}
          {aiSuggestion && (
            <div className="bg-gradient-to-br from-[#7C3AED]/5 to-[#E83E8C]/5 rounded-xl border border-[#7C3AED]/20 p-5" data-testid="ai-suggestions">
              <h3 className="text-sm font-bold text-[#7C3AED] mb-2 flex items-center gap-2"><Brain size={14} /> AI Schedule Optimization</h3>
              <div className="text-xs text-[#475569] whitespace-pre-wrap leading-relaxed">{aiSuggestion}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SmartSchedulerTab;
